import type { WebRtcConnectionStep } from "./connection-steps.js";

/** Milliseconds spent in each pipeline step, plus the wall-clock total. */
export interface StepTimings {
  /** Duration of each step, in the order it completed. */
  steps: Array<{ step: WebRtcConnectionStep; ms: number }>;
  totalMs: number;
}

export interface StepTimer {
  /** Close the previous step and open `step`. */
  mark: (step: WebRtcConnectionStep) => void;
  /** Close the final step and freeze the result. */
  finish: () => StepTimings;
  /** One-line breakdown for logs: `mic=12ms session=94ms …  total=740ms`. */
  summary: () => string;
}

const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

/**
 * Measures how long each connection step actually takes.
 *
 * Both pipelines previously logged step transitions as wall-clock strings, which
 * says when a step started but never how long it took — so there was no way to
 * tell whether a slow connect was the microphone, our signalling, or the SFU.
 */
export function createStepTimer(): StepTimer {
  const start = now();
  const steps: Array<{ step: WebRtcConnectionStep; ms: number }> = [];
  let openStep: WebRtcConnectionStep | null = null;
  let openedAt = start;
  let frozen: StepTimings | null = null;

  const close = (): void => {
    if (openStep == null) return;
    steps.push({ step: openStep, ms: now() - openedAt });
    openStep = null;
  };

  return {
    mark(step) {
      if (frozen) return;
      close();
      openStep = step;
      openedAt = now();
    },
    finish() {
      if (!frozen) {
        close();
        frozen = { steps, totalMs: now() - start };
      }
      return frozen;
    },
    summary() {
      const result = this.finish();
      const parts = result.steps.map(({ step, ms }) => `${step}=${Math.round(ms)}ms`);
      return `${parts.join(" ")}  total=${Math.round(result.totalMs)}ms`;
    },
  };
}
